import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Warning, Lightning, ArrowsClockwise } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import { ReversionContent } from './Window63Reversion';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGE_DISPLAY = { L: '1-10', ML: '11-20', M: '21-30', MH: '31-40', H: '41-50' };
const rl = (code) => RANGE_DISPLAY[code] || code;
const rlPat = (s) => s ? s.replace(/\b(L|ML|M|MH|H)\b/g, (m) => RANGE_DISPLAY[m] || m) : s;

export default function ReboundAlert() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return (t === 'reversion' || t === 'clustering' || t === 'rebound') ? t : 'rebound';
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/analysis/rebound-alert`);
      setData(res.data);
    } catch { toast.error('Failed to fetch rebound alert data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);

  if (loading) return <div className="flex items-center justify-center min-h-screen"><ArrowsClockwise size={32} className="animate-spin text-gray-400" /></div>;
  if (!data) return null;

  const { last_draw: ld, rebound_alert: ra, adjoining_analysis: aa, cluster_analysis: ca, number_frequency_2026: nf } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b sticky top-0 z-10 px-6 py-3">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-900"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">W46: Rebound & Reversion Engine</h1>
            <p className="text-xs text-gray-500">Extreme-draw rebound alerts · range clustering · hot/cold streak reversion timing (consolidated with former W63)</p>
          </div>
          <button onClick={fetchData} className="ml-auto text-gray-400 hover:text-blue-600"><ArrowsClockwise size={20} /></button>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 py-6 space-y-6">


        <WindowNarrative windowId="W46" {...NARRATIVES.W46} className="mb-5" />
        {/* Last Draw Info */}
        <div className="bg-white border rounded-lg p-4 flex items-center gap-6 flex-wrap">
          <div className="text-sm"><span className="text-gray-400">Last Draw:</span> <span className="font-bold text-gray-900">#{ld.draw_number}</span> <span className="text-gray-400">({ld.draw_date})</span></div>
          <div className="flex gap-1.5">{ld.balls.map((b, i) => <span key={i} className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">{b}</span>)}</div>
          <div className="text-sm"><span className="text-gray-400">Sum:</span> <span className="font-bold">{ld.sum}</span></div>
          <div className="text-sm"><span className="text-gray-400">Range:</span> <span className="font-mono font-bold">{ld.range_pattern}</span></div>
          <div className="text-sm"><span className="text-gray-400">B1+B2+B3:</span> <span className={`font-bold ${ld.b123_sum <= 10 ? 'text-red-600' : ld.b123_sum <= 20 ? 'text-amber-600' : 'text-gray-900'}`}>{ld.b123_sum}</span></div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 bg-white border rounded-lg p-1.5">
          <button onClick={() => setActiveTab('rebound')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${activeTab === 'rebound' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-rebound">
            Rebound Alert & Consecutive
          </button>
          <button onClick={() => setActiveTab('clustering')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${activeTab === 'clustering' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-clustering">
            Range Clustering & Number Frequency
          </button>
          <button onClick={() => setActiveTab('reversion')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${activeTab === 'reversion' ? 'bg-gradient-to-r from-orange-500 to-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-reversion">
            Reversion Analysis 🔥🧊
          </button>
        </div>

        {/* Tab 3: Reversion (embedded W63 content) */}
        {activeTab === 'reversion' && (
          <div className="bg-white border rounded-lg p-5">
            <ReversionContent embedded={true} />
          </div>
        )}

        {/* Tab 1: Rebound Alert & Consecutive Numbers */}
        {activeTab === 'rebound' && (<>

        {/* Rebound Alert */}
        {ra.level !== 'NONE' && (
          <div className={`border-2 rounded-xl p-5 ${ra.level === 'HIGH' ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`} data-testid="rebound-alert">
            <div className="flex items-center gap-3 mb-4">
              <Warning size={28} weight="fill" className={ra.level === 'HIGH' ? 'text-red-600' : 'text-amber-600'} />
              <div>
                <h2 className={`font-bold text-xl ${ra.level === 'HIGH' ? 'text-red-800' : 'text-amber-800'}`}>
                  {ra.level} REBOUND ALERT
                </h2>
                <p className="text-sm text-gray-600 mt-1">{ra.message}</p>
              </div>
            </div>

            {ra.data && typeof ra.data === 'object' && !Array.isArray(ra.data) && (
              <div className="space-y-4">
                {/* Average diffs */}
                <div className="grid grid-cols-5 gap-3">
                  {(ra.data.avg_diffs || []).map(d => (
                    <div key={d.ball} className="bg-white rounded-lg p-3 text-center border">
                      <div className="text-xs text-gray-400 font-bold">{d.ball}</div>
                      <div className={`font-mono font-bold text-xl ${d.avg_diff > 15 ? 'text-red-600' : d.avg_diff > 10 ? 'text-amber-600' : 'text-emerald-600'}`}>{d.avg_diff > 0 ? '+' : ''}{d.avg_diff}</div>
                      <div className="text-[10px] text-gray-400">avg diff</div>
                    </div>
                  ))}
                </div>

                {/* Rebound probability */}
                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center gap-3 mb-2">
                    <Lightning size={18} weight="fill" className="text-amber-500" />
                    <span className="font-bold text-gray-800">Full Rebound Probability (B1-B3 all &gt; 20):</span>
                    <span className="font-mono font-bold text-xl text-red-600">{ra.data.rebound_pct}%</span>
                    <span className="text-xs text-gray-400">({ra.data.historical_cases} historical cases)</span>
                  </div>
                </div>

                {/* Projected Numbers Per Ball */}
                {ra.data.per_ball_projection && (
                  <div className="bg-white rounded-lg p-4 border">
                    <h3 className="font-bold text-gray-800 mb-3">Projected Numbers Per Ball (after extreme low B1-B3)</h3>
                    <div className="grid grid-cols-5 gap-3">
                      {Object.entries(ra.data.per_ball_projection).map(([ball, proj]) => (
                        <div key={ball} className="border rounded-lg overflow-hidden">
                          <div className="bg-gray-800 text-white px-3 py-2 flex justify-between items-center">
                            <span className="font-bold">{ball}</span>
                            <span className="text-[10px] text-gray-300">last={proj.last} → ~{proj.projected_center}</span>
                          </div>
                          <div className="p-2">
                            <div className="text-[10px] text-gray-400 mb-1">Projected range: {proj.projected_range[0]}-{proj.projected_range[1]}</div>
                            <div className="flex flex-wrap gap-1">
                              {(proj.top_numbers || []).map((n, ni) => (
                                <span key={n.number} className={`text-xs font-mono font-bold px-1.5 py-0.5 rounded ${
                                  ni < 3 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                                }`}>{n.number}<span className="text-[8px] text-gray-400 ml-0.5">{n.pct}%</span></span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {ra.data.projected_sum && (
                      <div className="mt-3 bg-blue-50 rounded-lg p-3 flex items-center gap-4 text-sm">
                        <span className="font-bold text-blue-800">Projected Sum Range:</span>
                        <span className="font-mono font-bold text-blue-700">{ra.data.projected_sum.min} - {ra.data.projected_sum.max}</span>
                        <span className="text-xs text-gray-500">(avg: {ra.data.projected_sum.avg}, median: {ra.data.projected_sum.median})</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Top next patterns */}
                <div className="bg-white rounded-lg p-4 border">
                  <h3 className="font-bold text-gray-800 mb-3">Most Likely Next Range Patterns (after extreme low B1-B3)</h3>
                  <div className="grid grid-cols-5 gap-2">
                    {(ra.data.top_next_patterns || []).slice(0, 10).map((p, i) => (
                      <div key={p.pattern} className={`rounded-lg p-2.5 text-center border ${i < 3 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50'}`}>
                        <div className="font-mono font-bold text-lg text-gray-800">{rlPat(p.pattern)}</div>
                        <div className="text-xs text-gray-500">{p.count}x ({p.pct}%)</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent examples */}
                {(ra.data.recent_examples || []).length > 0 && (
                  <div className="bg-white rounded-lg p-4 border">
                    <h3 className="font-bold text-gray-800 mb-3">Recent Historical Examples</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-bold">Draw</th>
                            <th className="px-3 py-2 text-left text-xs font-bold">Previous</th>
                            <th className="px-3 py-2 text-left text-xs font-bold">Next</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">Diffs</th>
                            <th className="px-3 py-2 text-center text-xs font-bold">Next Pattern</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ra.data.recent_examples.map((ex, i) => (
                            <tr key={`${ra.id || ra.title || 'ra'}-ex-${ex.draw}-${i}`} className="border-b">
                              <td className="px-3 py-2 font-mono text-xs">#{ex.draw} ({ex.date})</td>
                              <td className="px-3 py-2 font-mono text-xs">{ex.prev.join(', ')}</td>
                              <td className="px-3 py-2 font-mono text-xs font-bold">{ex.curr.join(', ')}</td>
                              <td className="px-3 py-2 text-center font-mono text-xs">{ex.diffs.map(d => `${d > 0 ? '+' : ''}${d}`).join(', ')}</td>
                              <td className="px-3 py-2 text-center font-mono text-xs font-bold">{ex.curr_pat}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {ra.level === 'NONE' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5" data-testid="no-alert">
            <div className="flex items-center gap-3">
              <span className="text-emerald-600 font-bold text-lg">No Rebound Alert</span>
              <span className="text-sm text-gray-500">B1+B2+B3 sum = {ld.b123_sum} — within normal range. No extreme low detected.</span>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* ADJOINING PATTERNS ANALYSIS */}
        {/* ============================================================ */}
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="adjoining-analysis">
          <div className="bg-gradient-to-r from-indigo-700 to-purple-700 px-5 py-4 text-white">
            <h2 className="font-bold text-xl">Consecutive Numbers Analysis</h2>
            <p className="text-xs text-white/70 mt-1">How often do 2, 3, or 4+ consecutive numbers (e.g., 22,23 or 13,14,15) appear in a draw? Not same range — strictly back-to-back numbers.</p>
          </div>

          {/* Prediction bar */}
          <div className="bg-indigo-50 border-b px-5 py-3 flex items-center gap-6 flex-wrap">
            <div className="text-sm">
              <span className="text-gray-500">Draws with consecutive (2026):</span>
              <span className="font-bold text-indigo-700 ml-1">{aa.draws_with_adj_2026}/{aa.total_2026} ({aa.prediction.adj_probability_2026}%)</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Overall:</span>
              <span className="font-bold text-gray-700 ml-1">{aa.draws_with_adj_overall}/{aa.total_draws} ({aa.prediction.adj_probability_overall}%)</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Next draw consecutive probability:</span>
              <span className="font-bold text-indigo-700 ml-1">~{aa.prediction.adj_probability_2026}%</span>
            </div>
          </div>

          {/* Adjoining Matrix Table */}
          <div className="p-5">
            <h3 className="font-bold text-gray-900 mb-3">Consecutive Numbers Matrix: 2026 Draws</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-bold"></th>
                    <th className="px-3 py-2.5 text-center font-bold">1-10</th>
                    <th className="px-3 py-2.5 text-center font-bold">11-20</th>
                    <th className="px-3 py-2.5 text-center font-bold">21-30</th>
                    <th className="px-3 py-2.5 text-center font-bold">31-40</th>
                    <th className="px-3 py-2.5 text-center font-bold">41-50</th>
                    <th className="px-3 py-2.5 text-center font-bold bg-amber-700">Total 2026</th>
                    <th className="px-3 py-2.5 text-center font-bold bg-amber-700">%</th>
                    <th className="px-3 py-2.5 text-center font-bold bg-blue-700">Overall till 2025 %</th>
                  </tr>
                </thead>
                <tbody>
                  {(aa.matrix || []).map((row) => {
                    const isNoAdj = row.size === 0;
                    const bgClass = row.size >= 4 ? 'bg-red-50' : row.size === 3 ? 'bg-purple-50' : row.size === 2 ? 'bg-indigo-50/30' : isNoAdj ? 'bg-gray-50' : '';
                    const totalDraws = aa.total_2026 || 1;
                    return (
                      <tr key={row.size} className={`border-b ${bgClass}`}>
                        <td className="px-4 py-3 font-bold text-gray-800 text-base">{row.label}</td>
                        {['L','ML','M','MH','H'].map(rlk => {
                          const val = row[rlk]?.count_2026 || 0;
                          const pct = val > 0 ? Math.round(val / totalDraws * 1000) / 10 : 0;
                          return (
                            <td key={rlk} className={`px-3 py-2 text-center ${
                              isNoAdj ? 'text-gray-200' : val >= 3 ? 'text-purple-700' : val > 0 ? 'text-gray-800' : 'text-gray-200'
                            }`}>
                              {val > 0 && !isNoAdj ? (
                                <div>
                                  <div className="font-mono font-bold text-lg">{val}</div>
                                  <div className="text-[10px] text-gray-400">{pct}%</div>
                                </div>
                              ) : ''}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 text-center font-mono font-bold text-lg bg-amber-50 text-amber-800">{row.total_2026 || ''}</td>
                        <td className="px-3 py-3 text-center font-mono font-bold bg-amber-50 text-amber-700">{row.total_2026 > 0 ? `${row.pct_2026}%` : ''}</td>
                        <td className="px-3 py-3 text-center font-mono font-bold bg-blue-50 text-blue-700">{row.total_2025 > 0 ? `${row.pct_2025}%` : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Next Draw Projection */}
          {aa.prediction.next_projection && (
            <div className="px-5 pb-3">
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                <h4 className="font-bold text-purple-800 mb-3">Next Draw Consecutive Projection</h4>
                <p className="text-xs text-gray-500 mb-3">Based on transition: last draw {aa.prediction.current_state === 'has_adj' ? 'HAD' : 'had NO'} consecutive numbers. What typically follows?</p>
                <div className="grid grid-cols-5 gap-2">
                  {(aa.prediction.next_projection || []).slice(0, 10).map((p, i) => (
                    <div key={p.pattern} className={`rounded-lg p-2.5 text-center border ${
                      p.pattern === 'no_adj' ? 'bg-gray-50 border-gray-200' :
                      i < 3 ? 'bg-purple-100 border-purple-300' : 'bg-white border-gray-200'
                    }`}>
                      <div className={`font-bold text-sm ${p.pattern === 'no_adj' ? 'text-gray-500' : 'text-purple-800'}`}>
                        {p.pattern === 'no_adj' ? 'No Consecutive' : rlPat(p.pattern)}
                      </div>
                      <div className="text-xs text-gray-500">{p.count}x ({p.pct}%)</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Top Pairs Per Range */}
          {aa.prediction.top_pairs_by_range && Object.keys(aa.prediction.top_pairs_by_range).length > 0 && (
            <div className="px-5 pb-5">
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-bold text-gray-800 mb-3">Most Common Consecutive Pairs/Triples Per Range</h4>
                <div className="grid grid-cols-5 gap-3">
                    {[1,2,3,4,5].map((_, ci) => {
                      const rls = ['L','ML','M','MH','H'];
                      const rlCode = rls[ci];
                      const pairs = (aa.prediction.top_pairs_by_range || {})[rlCode] || [];
                      return (
                        <div key={rlCode} className="border rounded-lg overflow-hidden">
                          <div className="bg-gray-800 text-white px-3 py-2 text-center">
                            <span className="font-bold">{rl(rlCode)}</span>
                          </div>
                        <div className="p-2 space-y-1">
                          {pairs.length === 0 && <span className="text-xs text-gray-400 italic">None</span>}
                          {pairs.map((p, pi) => (
                            <div key={pi} className={`flex items-center justify-between text-xs ${pi === 0 ? 'font-bold' : ''}`}>
                              <span className="font-mono text-purple-700">[{p.numbers.join(',')}]</span>
                              <span className="text-gray-400">{p.count_overall}x{p.count_2026 > 0 ? ` (26:${p.count_2026})` : ''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}                </div>
              </div>
            </div>
          )}

          {/* 2026 draw-by-draw detail */}
          <div className="p-5 border-t">
            <h3 className="font-bold text-gray-900 mb-3">2026 Draws — Consecutive Numbers Detail</h3>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold">Draw</th>
                    <th className="px-3 py-2 text-left text-xs font-bold">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-bold">Numbers</th>
                    <th className="px-3 py-2 text-left text-xs font-bold">Consecutive Groups</th>
                  </tr>
                </thead>
                <tbody>
                  {(aa.draws_2026 || []).slice().reverse().map((d, i) => (
                    <tr key={d.draw} className={`border-b ${d.has_adjoining ? 'bg-purple-50/50' : ''}`}>
                      <td className="px-3 py-2 font-mono text-xs font-bold">#{d.draw}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{d.date}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {d.balls.map((b, bi) => {
                            const inCluster = d.clusters.some(cl => cl.numbers.includes(b));
                            return (
                              <span key={bi} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                inCluster ? 'bg-purple-600 text-white ring-2 ring-purple-300' : 'bg-gray-100 text-gray-600'
                              }`}>{b}</span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {d.clusters.length > 0 ? d.clusters.map((cl, ci) => (
                          <span key={ci} className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-[10px] font-bold px-2 py-1 rounded mr-1">
                            {cl.size} adj in {rl(cl.range)}: [{cl.numbers.join(',')}]
                          </span>
                        )) : <span className="text-xs text-gray-400 italic">None</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        </>)}

        {/* Tab 2: Range Clustering & Number Frequency */}
        {activeTab === 'clustering' && (<>

        {/* ============================================================ */}
        {/* RANGE CLUSTERING ANALYSIS */}
        {/* ============================================================ */}
        {ca && (
          <div className="bg-white border rounded-xl overflow-hidden" data-testid="cluster-analysis">
            <div className="bg-gradient-to-r from-rose-700 to-red-600 px-5 py-4 text-white">
              <h2 className="font-bold text-xl">Range Clustering: 3/4/5 Balls in Same Range</h2>
              <p className="text-xs text-white/70 mt-1">How many balls land in the same range bucket (1-10, 11-20, 21-30, 31-40, 41-50)? Not consecutive — just same range. E.g., [1, 4, 6, 10] = 4 balls in 1-10.</p>
            </div>

            {/* Stats bar */}
            <div className="bg-rose-50 border-b px-5 py-3 flex items-center gap-6 flex-wrap">
              <div className="text-sm">
                <span className="text-gray-500">Draws with 3+ cluster (2026):</span>
                <span className="font-bold text-rose-700 ml-1">{ca.draws_with_cluster_2026}/{ca.total_2026} ({ca.total_2026 ? Math.round(ca.draws_with_cluster_2026/ca.total_2026*1000)/10 : 0}%)</span>
              </div>
              <div className="text-sm">
                <span className="text-gray-500">Overall:</span>
                <span className="font-bold text-gray-700 ml-1">{ca.draws_with_cluster_overall}/{ca.total_draws} ({ca.total_draws ? Math.round(ca.draws_with_cluster_overall/ca.total_draws*1000)/10 : 0}%)</span>
              </div>
              <div className="text-sm">
                <span className="text-gray-500">Current state:</span>
                <span className={`font-bold ml-1 ${ca.current_state !== 'no_cluster' ? 'text-rose-700' : 'text-gray-500'}`}>{rlPat(ca.current_state)}</span>
              </div>
            </div>

            {/* Cluster Matrix Table */}
            <div className="p-5">
              <h3 className="font-bold text-gray-900 mb-3">Range Clustering Matrix: 2026 Draws</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-800 text-white">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-bold"></th>
                      <th className="px-3 py-2.5 text-center font-bold">1-10</th>
                      <th className="px-3 py-2.5 text-center font-bold">11-20</th>
                      <th className="px-3 py-2.5 text-center font-bold">21-30</th>
                      <th className="px-3 py-2.5 text-center font-bold">31-40</th>
                      <th className="px-3 py-2.5 text-center font-bold">41-50</th>
                      <th className="px-3 py-2.5 text-center font-bold bg-amber-700">Total 2026</th>
                      <th className="px-3 py-2.5 text-center font-bold bg-amber-700">%</th>
                      <th className="px-3 py-2.5 text-center font-bold bg-blue-700">Overall till 2025 %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ca.matrix || []).map((row, ri) => {
                      const bgClass = row.count >= 4 ? 'bg-red-50' : row.count === 3 ? 'bg-rose-50' : row.count === 2 ? 'bg-amber-50/30' : '';
                      const totalDraws = ca.total_2026 || 1;
                      return (
                        <tr key={row.count} className={`border-b ${bgClass}`}>
                          <td className="px-4 py-3 font-bold text-gray-800 text-base">{row.label}</td>
                          {['L','ML','M','MH','H'].map(rlk => {
                            const val = row[rlk]?.count_2026 || 0;
                            const pct = val > 0 ? Math.round(val / totalDraws * 1000) / 10 : 0;
                            return (
                              <td key={rlk} className={`px-3 py-2 text-center ${
                                val >= 3 ? 'text-red-600' : val > 0 ? 'text-gray-800' : 'text-gray-200'
                              }`}>
                                {val > 0 ? (
                                  <div>
                                    <div className="font-mono font-bold text-lg">{val}</div>
                                    <div className="text-[10px] text-gray-400">{pct}%</div>
                                  </div>
                                ) : ''}
                              </td>
                            );
                          })}
                          <td className="px-3 py-3 text-center font-mono font-bold text-lg bg-amber-50 text-amber-800">{row.total_2026 || ''}</td>
                          <td className="px-3 py-3 text-center font-mono font-bold bg-amber-50 text-amber-700">{row.total_2026 > 0 ? `${row.pct_2026}%` : ''}</td>
                          <td className="px-3 py-3 text-center font-mono font-bold bg-blue-50 text-blue-700">{row.total_2025 > 0 ? `${row.pct_2025}%` : ''}</td>
                        </tr>
                      );
                    })}
                    {(() => {
                      const matrix = ca.matrix || [];
                      const totalDraws = ca.total_2026 || 1;
                      const colSums = {};
                      ['L','ML','M','MH','H'].forEach(rlk => {
                        colSums[rlk] = matrix.reduce((s, row) => s + (row[rlk]?.count_2026 || 0), 0);
                      });
                      const grandTotal = matrix.reduce((s, row) => s + (row.total_2026 || 0), 0) || 1;
                      const colSumTotal = Object.values(colSums).reduce((a, b) => a + b, 0);
                      const pctDenom = colSumTotal || 1;
                      return (
                        <tr className="border-t-2 border-gray-800 bg-gray-100 font-bold">
                          <td className="px-4 py-3 text-gray-800 text-base">Total</td>
                          {['L','ML','M','MH','H'].map(rlk => {
                            const val = colSums[rlk];
                            const pct = val > 0 ? Math.round(val / pctDenom * 1000) / 10 : 0;
                            return (
                              <td key={rlk} className="px-3 py-2 text-center text-gray-800">
                                {val > 0 ? (
                                  <div>
                                    <div className="font-mono font-bold text-lg">{val}</div>
                                    <div className="text-[10px] text-gray-400">{pct}%</div>
                                  </div>
                                ) : ''}
                              </td>
                            );
                          })}
                          <td className="px-3 py-3 text-center font-mono font-bold text-lg bg-amber-50 text-amber-800">{colSumTotal}</td>
                          <td className="px-3 py-3 text-center bg-amber-50"></td>
                          <td className="px-3 py-3 text-center bg-blue-50"></td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Next Draw Projection */}
            {ca.next_projection && ca.next_projection.length > 0 && (
              <div className="px-5 pb-3">
                <div className="bg-gradient-to-r from-rose-50 to-red-50 rounded-lg p-4 border border-rose-200">
                  <h4 className="font-bold text-rose-800 mb-2">Next Draw Cluster Projection</h4>
                  <p className="text-xs text-gray-500 mb-3">Current state: <strong>{rlPat(ca.current_state)}</strong>. Based on transition — what typically follows?</p>
                  <div className="grid grid-cols-5 gap-2">
                    {ca.next_projection.slice(0, 10).map((p, i) => (
                      <div key={p.pattern} className={`rounded-lg p-2.5 text-center border ${
                        p.pattern === 'no_cluster' ? 'bg-gray-50 border-gray-200' :
                        i < 3 ? 'bg-rose-100 border-rose-300' : 'bg-white border-gray-200'
                      }`}>
                        <div className={`font-bold text-sm ${p.pattern === 'no_cluster' ? 'text-gray-500' : 'text-rose-800'}`}>
                          {p.pattern === 'no_cluster' ? 'No Cluster' : rlPat(p.pattern)}
                        </div>
                        <div className="text-xs text-gray-500">{p.count}x ({p.pct}%)</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 2026 Streaks */}
            {ca.streaks_2026 && ca.streaks_2026.length > 0 && (
              <div className="px-5 pb-3">
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <h4 className="font-bold text-amber-800 mb-2">2026 Clustering Streaks (consecutive draws with cluster in same range)</h4>
                  <div className="flex gap-3 flex-wrap">
                    {ca.streaks_2026.map((s, i) => (
                      <span key={`s2026-${s.range}-${i}`} className="bg-amber-100 text-amber-800 font-bold text-sm px-3 py-1.5 rounded-lg">
                        {rl(s.range)}: {s.length} consecutive (draws {s.draws.join(', ')})
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* NUMBER FREQUENCY IN 2026 */}
        {/* ============================================================ */}
        {nf && (
          <div className="bg-white border rounded-xl overflow-hidden" data-testid="number-frequency-2026">
            <div className="bg-gradient-to-r from-emerald-700 to-teal-600 px-5 py-4 text-white">
              <h2 className="font-bold text-xl">2026 Number Frequency (1-50)</h2>
              <p className="text-xs text-white/70 mt-1">How many times each number appeared across {nf.total_draws} draws in 2026. Grouped by range.</p>
            </div>

            {/* Grid by range */}
            <div className="p-5">
              <div className="grid grid-cols-5 gap-4">
                {['L','ML','M','MH','H'].map(rlk => {
                  const nums = nf.by_range?.[rlk] || [];
                  const maxCount = Math.max(...nums.map(n => n.count), 1);
                  return (
                    <div key={rlk} className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-800 text-white px-3 py-2 text-center">
                        <span className="font-bold">{rl(rlk)}</span>
                      </div>
                      <div className="p-2 space-y-1">
                        {nums.map(n => {
                          const barWidth = n.count > 0 ? Math.round(n.count / maxCount * 100) : 0;
                          const isHot = n.count >= 5;
                          const isCold = n.count <= 1;
                          const isInLast = ld.balls.includes(n.number);
                          return (
                            <div key={n.number} className={`flex items-center gap-2 text-xs ${isInLast ? 'bg-blue-50 rounded' : ''}`}>
                              <span className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${
                                isHot ? 'bg-emerald-600 text-white' : isCold ? 'bg-gray-200 text-gray-400' : 'bg-gray-100 text-gray-700'
                              }`}>{n.number}</span>
                              <div className="flex-1 relative h-5">
                                <div className={`absolute inset-y-0 left-0 rounded ${
                                  isHot ? 'bg-emerald-200' : isCold ? 'bg-gray-100' : 'bg-amber-100'
                                }`} style={{ width: `${barWidth}%` }} />
                                <span className="relative z-10 font-mono font-bold text-xs px-1">{n.count}</span>
                              </div>
                              <span className="text-[10px] text-gray-400 w-10 text-right">{n.pct}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary stats */}
              <div className="mt-4 bg-gray-50 rounded-lg p-4 flex gap-8 flex-wrap text-sm">
                <div>
                  <span className="text-gray-500">Hot (5+):</span>
                  <span className="font-bold text-emerald-700 ml-1">{(nf.numbers || []).filter(n => n.count >= 5).length} numbers</span>
                </div>
                <div>
                  <span className="text-gray-500">Cold (0-1):</span>
                  <span className="font-bold text-red-600 ml-1">{(nf.numbers || []).filter(n => n.count <= 1).length} numbers</span>
                </div>
                <div>
                  <span className="text-gray-500">Never appeared:</span>
                  <span className="font-bold text-gray-800 ml-1">{(nf.numbers || []).filter(n => n.count === 0).map(n => n.number).join(', ') || 'None'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Number Grid with Last 10 Draws */}
        {nf && ca && ca.draws_2026 && (() => {
          const allNums = (nf.numbers || []).slice().sort((a, b) => a.number - b.number);
          const last10 = (ca.draws_2026 || []).slice().reverse().slice(0, 10);
          return (
            <div className="bg-white border rounded-xl overflow-hidden" data-testid="number-grid-last10">
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-5 py-4 text-white">
                <h2 className="font-bold text-xl">Number Grid: Frequency + Last 10 Draws</h2>
                <p className="text-xs text-white/70 mt-1">All 50 numbers with 2026 frequency count and which numbers appeared in the last 10 draws.</p>
              </div>
              <div className="p-4 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-center font-bold text-gray-700 w-10">No.</th>
                      <th className="px-2 py-2 text-center font-bold text-gray-700 w-10">Count</th>
                      {last10.map(dd => (
                        <th key={dd.draw} className="px-1 py-2 text-center font-bold text-gray-500 text-[9px] w-8">
                          <div>{dd.date?.split('/').slice(0, 2).join('/')}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allNums.map(n => {
                      const isHot = n.count >= 5;
                      const isCold = n.count <= 1;
                      const isZero = n.count === 0;
                      const rangeStart = Math.floor((n.number - 1) / 10) * 10 + 1;
                      const isRangeBorder = n.number === rangeStart;
                      return (
                        <tr key={n.number} className={`${isRangeBorder && n.number > 1 ? 'border-t-2 border-gray-300' : 'border-b border-gray-50'} ${isHot ? 'bg-emerald-50/40' : isZero ? 'bg-red-50/30' : ''}`}>
                          <td className="px-2 py-1.5 text-center">
                            <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-bold text-xs ${
                              isHot ? 'bg-emerald-600 text-white' : isCold ? 'bg-gray-200 text-gray-400' : 'bg-gray-100 text-gray-700'
                            }`}>{n.number}</span>
                          </td>
                          <td className={`px-2 py-1.5 text-center font-mono font-bold text-base ${
                            isHot ? 'text-emerald-700' : isZero ? 'text-red-400' : 'text-gray-700'
                          }`}>{n.count}</td>
                          {last10.map(dd => {
                            const appeared = dd.balls.includes(n.number);
                            return (
                              <td key={dd.draw} className="px-1 py-1.5 text-center">
                                {appeared ? (
                                  <span className="inline-flex w-6 h-6 rounded bg-blue-600 text-white items-center justify-center font-bold text-[10px]">X</span>
                                ) : null}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* 2026 Draws — Range Cluster Detail (moved below Number Frequency) */}
        {ca && ca.draws_2026 && (
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-rose-700 to-red-600 px-5 py-4 text-white">
              <h3 className="font-bold text-xl">2026 Draws — Range Cluster Detail</h3>
              <p className="text-xs text-white/70 mt-1">Per-draw breakdown: how many balls in each range, with cluster highlights.</p>
            </div>
            <div className="p-5">
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-bold">Draw</th>
                      <th className="px-3 py-2 text-left text-xs font-bold">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-bold">Numbers</th>
                      <th className="px-3 py-2 text-center text-xs font-bold">1-10</th>
                      <th className="px-3 py-2 text-center text-xs font-bold">11-20</th>
                      <th className="px-3 py-2 text-center text-xs font-bold">21-30</th>
                      <th className="px-3 py-2 text-center text-xs font-bold">31-40</th>
                      <th className="px-3 py-2 text-center text-xs font-bold">41-50</th>
                      <th className="px-3 py-2 text-left text-xs font-bold">Cluster</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ca.draws_2026 || []).slice().reverse().map((d) => {
                      const dist = d.distribution || {};
                      return (
                        <tr key={d.draw} className={`border-b ${d.has_cluster ? 'bg-rose-50/50' : ''}`}>
                          <td className="px-3 py-2 font-mono text-xs font-bold">#{d.draw}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{d.date}</td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1">
                              {d.balls.map((b, bi) => {
                                const inCluster = d.clusters.some(cl => cl.numbers.includes(b));
                                return (
                                  <span key={bi} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                    inCluster ? 'bg-rose-600 text-white ring-2 ring-rose-300' : 'bg-gray-100 text-gray-600'
                                  }`}>{b}</span>
                                );
                              })}
                            </div>
                          </td>
                          {['L','ML','M','MH','H'].map(rlc => {
                            const cnt = dist[rlc] || 0;
                            return (
                              <td key={rlc} className={`px-3 py-2 text-center font-mono font-bold ${
                                cnt >= 4 ? 'text-red-700 bg-red-100' : cnt >= 3 ? 'text-rose-600 bg-rose-50' : cnt > 0 ? 'text-gray-600' : 'text-gray-200'
                              }`}>{cnt || '-'}</td>
                            );
                          })}
                          <td className="px-3 py-2">
                            {d.clusters.length > 0 ? d.clusters.map((cl, ci) => (
                              <span key={ci} className="inline-flex items-center bg-rose-100 text-rose-800 text-[10px] font-bold px-2 py-1 rounded mr-1">
                                {cl.count} in {rl(cl.range)}
                              </span>
                            )) : <span className="text-xs text-gray-400 italic">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        </>)}
      </main>
    </div>
  );
}
